import React, {useState, useEffect, useRef, memo} from "react";
import {Box, Flex, Text, Spinner, Checkbox} from "@chakra-ui/react";
import {fileProxy} from "./helpers/fileProxy";
import {previewFile} from "./helpers/previewFile";
import {extractFileName, fileDate, fileDateTime} from "./helpers/extractFileName";
import "./fileCards.css";

const thumbnailCache = new Map();
const thumbnailFailureCache = new Set();
const FILE_FETCH_TIMEOUT_MS = 15000;
const PDF_PARSE_TIMEOUT_MS = 7000;
const PDF_PARSE_FALLBACK_TIMEOUT_MS = 7000;
const PDF_RENDER_TIMEOUT_MS = 12000;

function withTimeout(promise, ms, onTimeout) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      try {
        onTimeout?.();
      } catch {}
      reject(new Error("timeout"));
    }, ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
}

function copyPdfBytes(data) {
  if (data instanceof ArrayBuffer) return new Uint8Array(data.slice(0));
  if (ArrayBuffer.isView(data)) {
    return new Uint8Array(
      data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength)
    );
  }
  throw new Error("unsupported pdf data type");
}

let pdfjsLib = null;
const initPdfJs = async () => {
  if (pdfjsLib) return pdfjsLib;
  try {
    const lib = await import("pdfjs-dist/legacy/build/pdf");
    pdfjsLib = lib.default || lib;
    if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
    }
    return pdfjsLib;
  } catch {
    return null;
  }
};

const FILE_TYPE_STYLES = {
  pdf: {color: "#dc2626", bg: "#fef2f2", label: "PDF"},
  doc: {color: "#2563eb", bg: "#eff6ff", label: "DOC"},
  docx: {color: "#2563eb", bg: "#eff6ff", label: "DOCX"},
  xls: {color: "#16a34a", bg: "#f0fdf4", label: "XLS"},
  xlsx: {color: "#16a34a", bg: "#f0fdf4", label: "XLSX"},
  ppt: {color: "#ea580c", bg: "#fff7ed", label: "PPT"},
  pptx: {color: "#ea580c", bg: "#fff7ed", label: "PPTX"},
  csv: {color: "#16a34a", bg: "#f0fdf4", label: "CSV"},
  txt: {color: "#57534e", bg: "#fafaf9", label: "TXT"},
  json: {color: "#ca8a04", bg: "#fefce8", label: "JSON"},
  png: {color: "#0891b2", bg: "#ecfeff", label: "PNG"},
  jpg: {color: "#0891b2", bg: "#ecfeff", label: "JPG"},
  jpeg: {color: "#0891b2", bg: "#ecfeff", label: "JPEG"},
  gif: {color: "#0891b2", bg: "#ecfeff", label: "GIF"},
  webp: {color: "#0891b2", bg: "#ecfeff", label: "WEBP"},
  svg: {color: "#0891b2", bg: "#ecfeff", label: "SVG"},
  heic: {color: "#0891b2", bg: "#ecfeff", label: "HEIC"},
  mp4: {color: "#7c3aed", bg: "#f5f3ff", label: "MP4"},
  mp3: {color: "#7c3aed", bg: "#f5f3ff", label: "MP3"},
  mov: {color: "#7c3aed", bg: "#f5f3ff", label: "MOV"},
  avi: {color: "#7c3aed", bg: "#f5f3ff", label: "AVI"},
};

function getFileStyle(name) {
  const ext = (name || "").toLowerCase().split(".").pop();
  return (
    FILE_TYPE_STYLES[ext] || {
      color: "#78716c",
      bg: "#f5f5f4",
      label: ext?.toUpperCase() || "FILE",
    }
  );
}

function FileCard({
  dashState,
  basePath,
  name,
  storedDisplayName,
  storedAddedAt,
  storedLastModified,
  contextPath = "",
  setSelectedPath,
  setSelectedFile,
  selectedPath,
  selectedFile,
  setPreview,
  toast,
  selectedFiles = [],
  setSelectedFiles,
}) {
  const fullPath = basePath + name;
  const [thumbnail, setThumbnail] = useState(
    thumbnailCache.get(fullPath) || null
  );
  const [isLoadingThumb, setIsLoadingThumb] = useState(false);
  const [showCheckbox, setShowCheckbox] = useState(false);
  const mountedRef = useRef(true);

  const displayName = storedDisplayName || extractFileName(name, "name");
  const uploadedDate = fileDate(storedAddedAt, storedLastModified, name);
  const uploadedDateTime = fileDateTime(storedAddedAt, storedLastModified, name);
  const fileStyle = getFileStyle(name);
  const fileExt = (name || "").toLowerCase().split(".").pop();
  const isImage = ["png", "jpg", "jpeg", "gif", "webp", "svg", "heic"].includes(fileExt);
  const isPdf = fileExt === "pdf";
  const canGenerateThumb = isImage || isPdf;

  const isSelected = selectedPath === basePath && selectedFile === name;
  const isChecked = selectedFiles.includes(fullPath);
  const isAnalyzable = ["pdf", "png", "jpg", "jpeg"].includes(fileExt);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!canGenerateThumb || thumbnailFailureCache.has(fullPath) || thumbnail)
      return;
    let cancelled = false;

    const loadThumbnail = async () => {
      setIsLoadingThumb(true);
      try {
        const file = await withTimeout(
          fileProxy(
            dashState,
            dashState.organization?.s3BucketName || "brightlighthealth",
            basePath,
            name
          ),
          FILE_FETCH_TIMEOUT_MS
        );
        if (cancelled) return;

        const contentType =
          file.headers?.["content-type"] ||
          file.headers?.["Content-Type"] ||
          "application/octet-stream";

        if (isImage) {
          const blob = new Blob([file.data], {type: contentType});
          const blobUrl = URL.createObjectURL(blob);
          if (!cancelled && mountedRef.current) {
            setThumbnail(blobUrl);
            thumbnailCache.set(fullPath, blobUrl);
          }
        } else if (isPdf) {
          let loadingTask = null;
          let pdf = null;
          try {
            const lib = await initPdfJs();
            if (!lib) throw new Error("pdfjs not available");
            const getPdfDocument = (disableWorker) =>
              lib.getDocument({
                data: copyPdfBytes(file.data),
                disableWorker,
                disableAutoFetch: true,
                disableRange: true,
                disableStream: true,
                stopAtErrors: true,
                isEvalSupported: false,
                useSystemFonts: true,
              });

            loadingTask = getPdfDocument(false);
            try {
              pdf = await withTimeout(
                loadingTask.promise,
                PDF_PARSE_TIMEOUT_MS,
                () => loadingTask?.destroy?.()
              );
            } catch {
              loadingTask?.destroy?.();
              loadingTask = getPdfDocument(true);
              pdf = await withTimeout(
                loadingTask.promise,
                PDF_PARSE_FALLBACK_TIMEOUT_MS,
                () => loadingTask?.destroy?.()
              );
            }

            const page = await pdf.getPage(1);
            const viewport = page.getViewport({scale: 0.4});
            const canvas = document.createElement("canvas");
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            await withTimeout(
              page.render({
                canvasContext: canvas.getContext("2d"),
                viewport,
              }).promise,
              PDF_RENDER_TIMEOUT_MS
            );
            const dataUrl = canvas.toDataURL("image/jpeg", 0.6);
            if (!cancelled && mountedRef.current) {
              setThumbnail(dataUrl);
              thumbnailCache.set(fullPath, dataUrl);
            }
            try {
              pdf?.cleanup?.();
              pdf?.destroy?.();
            } catch {}
          } catch {
            try {
              loadingTask?.destroy?.();
            } catch {}
            try {
              pdf?.destroy?.();
            } catch {}
            thumbnailFailureCache.add(fullPath);
          }
        }
      } catch {
        // Show placeholder on error
      } finally {
        if (!cancelled && mountedRef.current) setIsLoadingThumb(false);
      }
    };

    const delay = Math.random() * 300;
    const timer = setTimeout(loadThumbnail, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [canGenerateThumb, fullPath]);

  const onClick = (e) => {
    setSelectedPath(basePath);
    setSelectedFile(name);
    if ((e.ctrlKey || e.metaKey) && isAnalyzable) {
      handleCheckboxChange(e);
    }
  };

  const onDoubleClick = (e) => {
    e.stopPropagation();
    previewFile(dashState, basePath, name, setPreview, toast);
  };

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    if (!setSelectedFiles) return;
    if (isChecked) {
      setSelectedFiles(selectedFiles.filter((f) => f !== fullPath));
    } else {
      setSelectedFiles([...selectedFiles, fullPath]);
    }
  };

  return (
    <Box
      position="relative"
      cursor="pointer"
      borderRadius="10px"
      overflow="hidden"
      border="1.5px solid"
      borderColor={
        isSelected
          ? "#3b82f6"
          : isChecked
          ? "#93c5fd"
          : "rgba(0,0,0,0.06)"
      }
      bg="white"
      transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
      boxShadow={
        isSelected
          ? "0 0 0 3px rgba(59,130,246,0.1)"
          : "0 1px 3px rgba(0,0,0,0.04)"
      }
      _hover={{
        borderColor: isSelected ? "#3b82f6" : "rgba(0,0,0,0.12)",
        transform: "translateY(-2px)",
        boxShadow: isSelected
          ? "0 0 0 3px rgba(59,130,246,0.1), 0 8px 20px rgba(0,0,0,0.08)"
          : "0 8px 20px rgba(0,0,0,0.08)",
      }}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onMouseEnter={() => setShowCheckbox(true)}
      onMouseLeave={() => {
        if (!isChecked) setShowCheckbox(false);
      }}
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
      className="file-card"
    >
      <Box
        h="100px"
        bg={thumbnail ? (isPdf ? "white" : "gray.50") : fileStyle.bg}
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        position="relative"
      >
        {isLoadingThumb ? (
          <Spinner size="xs" color={fileStyle.color} opacity={0.3} />
        ) : thumbnail ? (
          <Box
            as="img"
            src={thumbnail}
            alt={displayName}
            w="100%"
            h="100%"
            objectFit={isPdf ? "contain" : "cover"}
            draggable={false}
            style={{userSelect: "none"}}
          />
        ) : (
          <Text
            fontSize="20px"
            fontWeight="800"
            color={fileStyle.color}
            opacity={0.2}
            letterSpacing="0.05em"
            userSelect="none"
          >
            {fileStyle.label}
          </Text>
        )}

        {isAnalyzable && (showCheckbox || isChecked) && (
          <Box
            position="absolute"
            top="8px"
            left="8px"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              isChecked={isChecked}
              onChange={handleCheckboxChange}
              size="sm"
              colorScheme="blue"
              bg="white"
              borderRadius="4px"
              boxShadow="0 1px 4px rgba(0,0,0,0.12)"
              onClick={(e) => e.stopPropagation()}
              sx={{
                "& .chakra-checkbox__control": {
                  boxShadow: "none",
                  _focus: {boxShadow: "none"},
                  _focusVisible: {boxShadow: "none"},
                },
              }}
            />
          </Box>
        )}
      </Box>

      <Box px={3} py={2.5} borderTop="1px solid" borderColor="rgba(0,0,0,0.04)">
        <Flex align="center" gap={1.5} mb={1}>
          <Box
            px={1.5}
            py="1px"
            borderRadius="3px"
            bg={fileStyle.bg}
            flexShrink={0}
          >
            <Text
              fontSize="9px"
              fontWeight="700"
              color={fileStyle.color}
              textTransform="uppercase"
              letterSpacing="0.03em"
            >
              {fileStyle.label}
            </Text>
          </Box>
        </Flex>
        <Text
          fontSize="11.5px"
          fontWeight="500"
          color="gray.700"
          noOfLines={contextPath ? 1 : 2}
          lineHeight="1.4"
          title={
            uploadedDateTime
              ? `${displayName}\n${uploadedDateTime}`
              : displayName
          }
          wordBreak="break-word"
        >
          {displayName}
        </Text>
        {uploadedDate && (
          <Text
            mt={0.5}
            fontSize="10px"
            color="gray.500"
            noOfLines={1}
            title={uploadedDateTime}
          >
            {uploadedDate}
          </Text>
        )}
        {contextPath && (
          <Text
            mt={0.5}
            fontSize="10px"
            color="gray.500"
            noOfLines={1}
            title={contextPath}
          >
            {contextPath}
          </Text>
        )}
      </Box>
    </Box>
  );
}

export default memo(FileCard);
